import React, { useMemo, useState } from "react";

import { InfoTooltip } from "../BuilderUtils";
import { normalize } from "../layerSearch";
import { buildDecisionIndex, searchDecisions } from "../decisionSearch";
import { bandsOf, poolIdsOf, selectionOf } from "../measurePool";

/**
 * LE RÉGLAGE DE TIRAGE D'UNE PAGE PROBABILISTE.
 *
 * La page ne porte plus la liste de ses mesures : elle porte les RÈGLES qui la
 * produiront, tour après tour. Quatre réglages, et rien de plus tant que le
 * reste n'a pas fait ses preuves : combien de cases, quel équilibre entre
 * coûteuses et peu coûteuses, ce qu'on épingle, ce qu'on écarte.
 *
 * LES PLANCHERS SONT DES MINIMUMS, PAS DES QUOTAS EXACTS. « Au moins deux peu
 * coûteuses et deux coûteuses » dit exactement l'intention — avoir de quoi
 * arbitrer — et laisse le reste des cases varier d'une séance à l'autre. Des
 * quotas exacts figeraient la grille et, surtout, deviendraient intenables dès
 * que le réservoir se vide : un plancher qu'on ne peut plus servir dégrade,
 * un quota qu'on ne peut plus servir bloque.
 *
 * ÉPINGLER ET ÉCARTER SONT DEUX ÉTATS D'UNE MÊME MESURE, pas deux listes. Une
 * mesure du réservoir est, pour cette page, tirée au sort (le défaut), toujours
 * proposée, ou jamais proposée — trois positions d'un même sélecteur, sur une
 * seule liste. Deux blocs séparés (un sélecteur pour épingler, un nuage de
 * puces pour écarter) obligeaient à chercher la même mesure à deux endroits, et
 * permettaient d'épingler une mesure hors réservoir — que le tirage ignore
 * désormais (`forced_outside_pool`). Le choix ne se fait donc que parmi les
 * mesures du réservoir ; une page écrite avant qui en épingle une autre la voit
 * signalée, avec de quoi la retirer, et rien pour en ajouter.
 *
 * CE QUI EST MONTRÉ À CÔTÉ : la variance. Un tirage d'exemple ne dit rien de ce
 * que verront les participants ; dix tirages disent si le réglage produit
 * régulièrement ce qu'on croit.
 */

// Au-delà, la liste reçoit un champ de recherche. Le même seuil que la
// section Réservoir de l'atelier : en dessous, tout tient sous les yeux.
const SEARCH_ABOVE = 8;

// Les trois positions d'une mesure du réservoir pour cette page.
const ROLES = [
  { id: "draw", label: "Tirage", title: "Tirée au sort avec les autres" },
  { id: "pin", label: "Toujours", title: "Toujours proposée sur cette page — sauf si elle a déjà été prise plus tôt dans la partie" },
  { id: "skip", label: "Jamais", title: "Jamais proposée sur cette page — elle reste disponible aux autres tours" },
];

export default function PoolSelectionInspector({
  page, onPatchContent, ctx, preview, onReroll,
}) {
  const [search, setSearch] = useState("");

  const config = ctx.config;
  const selection = selectionOf(page);
  const bands = bandsOf(config);
  const poolIds = poolIdsOf(config);

  const patchSelection = (partial) => {
    onPatchContent({
      selection: { ...(page.content?.selection || {}), mode: "pool", ...partial },
    });
  };

  const setFloor = (bandId, value) => {
    const floors = { ...(page.content?.selection?.min_per_band || {}) };
    const floor = Number(value) || 0;
    if (floor > 0) floors[bandId] = floor;
    else delete floors[bandId];
    patchSelection({ min_per_band: floors });
  };

  // UNE MESURE N'A QU'UN RÔLE : la mettre en « Toujours » la retire de
  // « Jamais », et inversement. Sinon les deux listes pourraient la porter à la
  // fois, et le tirage trancherait sans que l'auteur l'ait vu.
  const roleOf = (decisionId) => {
    if (selection.include.includes(decisionId)) return "pin";
    if (selection.exclude.includes(decisionId)) return "skip";
    return "draw";
  };
  const setRole = (decisionId, role) => {
    const without = (list) => list.filter((d) => d !== decisionId);
    patchSelection({
      include: role === "pin" ? [...without(selection.include), decisionId] : without(selection.include),
      exclude: role === "skip" ? [...without(selection.exclude), decisionId] : without(selection.exclude),
    });
  };

  const totalFloor = Object.values(selection.minPerBand).reduce((a, b) => a + b, 0);
  const decisionsById = useMemo(
    () => Object.fromEntries((ctx.allDecisions || []).map((d) => [d._id, d])),
    [ctx.allDecisions]
  );
  const titleOf = (id) => decisionsById[id]?.title || id;
  const bandLabel = (bandId) => bands.find((b) => b.id === bandId)?.label || bandId;

  /* ── La liste du réservoir ───────────────────────────────────────── */

  const poolDecisions = useMemo(
    () => poolIds.map((id) => decisionsById[id]).filter(Boolean),
    [poolIds, decisionsById]
  );
  const poolIndex = useMemo(() => buildDecisionIndex(poolDecisions), [poolDecisions]);

  const trimmedQuery = search.trim();
  const matchedIds = useMemo(() => {
    if (!trimmedQuery) return null;
    return new Set(searchDecisions(poolIndex, { query: trimmedQuery }).map((d) => d._id));
  }, [poolIndex, trimmedQuery]);

  const rows = poolIds
    .filter((id) => (
      !matchedIds || matchedIds.has(id) || normalize(id).includes(normalize(trimmedQuery))
    ))
    .sort((a, b) => titleOf(a).localeCompare(titleOf(b), "fr"));

  // Épinglées HORS réservoir : le tirage les ignore. On ne peut plus en créer
  // d'ici ; celles qui restent sont signalées, avec un seul geste possible.
  const strayPins = selection.include.filter((id) => !poolIds.includes(id));

  const pinnedCount = selection.include.length - strayPins.length;
  const skippedCount = selection.exclude.filter((id) => poolIds.includes(id)).length;

  return (
    <div>
      {poolIds.length === 0 && (
        <div className="builder-warning-inline">
          Le réservoir de cet atelier est vide : cette page s'afficherait sans aucune mesure.
          Garnissez-le dans la section « Réservoir de mesures » de l'atelier.
        </div>
      )}

      <label className="builder-label">
        <span className="field-label-row">
          Nombre de cases
          <InfoTooltip text="Combien de mesures s'afficheront. La mosaïque n'utilise sa grille 2×4 qu'à exactement 8 cartes — en dessous, la disposition change." />
        </span>
        <input
          type="number"
          min="1"
          className="builder-input small"
          value={page.content?.selection?.count ?? ""}
          onChange={(e) => patchSelection({ count: Number(e.target.value) || 0 })}
        />
      </label>

      <h3 className="builder-subsection-title">
        <span className="field-label-row">
          Équilibre des coûts
          <InfoTooltip text="Le minimum garanti par bande de coût. Le reste des cases se remplit librement dans le réservoir : c'est ce qui fait varier la grille d'une séance à l'autre." />
        </span>
      </h3>
      <div className="builder-pool-floors">
        {bands.map((band) => (
          <label key={band.id} className="builder-pool-floor">
            <span>{band.label || band.id}</span>
            <span>au moins</span>
            <input
              type="number"
              min="0"
              placeholder="0"
              value={page.content?.selection?.min_per_band?.[band.id] ?? ""}
              onChange={(e) => setFloor(band.id, e.target.value)}
            />
          </label>
        ))}
      </div>
      {totalFloor > selection.count && (
        <div className="builder-warning-inline">
          Les planchers demandent {totalFloor} mesures pour {selection.count} cases : les
          derniers ne pourront jamais être tenus.
        </div>
      )}

      {strayPins.length > 0 && (
        <div className="builder-warning-inline">
          <div>
            {strayPins.length > 1
              ? `${strayPins.length} mesures épinglées ne sont pas dans le réservoir`
              : "Une mesure épinglée n'est pas dans le réservoir"}
            {" "}: le tirage les ignore. Ajoutez-les au réservoir de l'atelier, ou désépinglez-les.
          </div>
          <div className="builder-pile" style={{ marginTop: 6 }}>
            {strayPins.map((decisionId) => (
              <div key={decisionId} className="builder-pile-item">
                <div className="builder-pile-info">
                  <span className="builder-pile-id">{decisionId}</span>
                  <span className="builder-pile-title">{titleOf(decisionId)}</span>
                </div>
                <button
                  className="builder-icon-btn danger"
                  onClick={() => setRole(decisionId, "draw")}
                  title="Ne plus épingler"
                >✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {poolIds.length > 0 && (
        <>
          <h3 className="builder-subsection-title">
            <span className="field-label-row">
              Mesures du réservoir ({poolIds.length})
              <InfoTooltip text="Pour chaque mesure : tirée au sort (le défaut), toujours proposée sur cette page, ou jamais. Une mesure « toujours » déjà prise plus tôt dans la partie ne revient pas ; une mesure « jamais » reste disponible aux autres tours, et revient quand même ici si le réservoir ne suffit plus à remplir la grille." />
            </span>
            <span className="builder-pool-role-summary">
              {pinnedCount > 0 && (
                <span className="builder-chip role-pin">{pinnedCount} toujours</span>
              )}
              {skippedCount > 0 && (
                <span className="builder-chip role-skip">{skippedCount} jamais</span>
              )}
              {pinnedCount === 0 && skippedCount === 0 && (
                <span className="builder-hint" style={{ margin: 0 }}>tout est tiré au sort</span>
              )}
            </span>
          </h3>

          {poolIds.length > SEARCH_ABOVE && (
            <input
              className="builder-search small builder-pool-roles-search"
              placeholder="Rechercher dans le réservoir…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Rechercher dans le réservoir"
            />
          )}

          <div className="builder-pool-rows builder-pool-roles">
            {rows.map((decisionId) => {
              const decision = decisionsById[decisionId];
              const role = roleOf(decisionId);
              const band = preview?.classification?.[decisionId]?.band;
              return (
                <div
                  key={decisionId}
                  className={`builder-pool-row role-${role}`}
                  data-testid={`pool-row-${decisionId}`}
                >
                  <div className="builder-pool-row-main">
                    <span className="builder-pool-row-title">{decision?.title || decisionId}</span>
                    <span className="builder-pool-row-meta">
                      <code className="builder-pool-row-id">{decisionId}</code>
                      {band && (
                        <span className={`builder-chip subtle band-${band}`}>{bandLabel(band)}</span>
                      )}
                      {!decision && (
                        <span className="builder-pile-missing">⚠ Mesure introuvable en base</span>
                      )}
                    </span>
                  </div>
                  <div
                    className="builder-toggle-group builder-pool-role"
                    role="radiogroup"
                    aria-label={`Rôle de ${decision?.title || decisionId}`}
                  >
                    {ROLES.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={role === option.id}
                        className={`builder-toggle-btn${role === option.id ? ` active is-${option.id}` : ""}`}
                        title={option.title}
                        onClick={() => setRole(decisionId, option.id)}
                      >{option.label}</button>
                    ))}
                  </div>
                </div>
              );
            })}
            {rows.length === 0 && (
              <div className="builder-empty">Aucune mesure du réservoir ne correspond.</div>
            )}
          </div>
        </>
      )}

      <PreviewSummary preview={preview} bands={bands} onReroll={onReroll} />
    </div>
  );
}

/** Les codes que le tirage remonte quand il a dû relâcher quelque chose. */
const NOTE_LABELS = {
  band_floor_unmet: "un plancher de bande n'a pas pu être tenu",
  pool_exhausted: "le réservoir ne suffit pas à remplir la grille",
  exclusion_relaxed: "des mesures écartées ont dû être réintégrées",
  budget_rescue: "le tirage était trop cher : une mesure abordable a été substituée",
  no_affordable_measure: "aucune mesure n'est prenable avec le budget de ce tour",
  forced_already_taken: "une mesure épinglée avait déjà été prise",
  forced_outside_pool: "une mesure épinglée n'est pas dans le réservoir",
  forced_unknown: "une mesure épinglée n'existe pas dans la base",
  forced_over_count: "plus de mesures épinglées que de cases",
};

/**
 * CE QUE DIX TIRAGES DISENT, ET QU'UN SEUL NE DIT PAS. Un aperçu unique se lit
 * comme une maquette : l'auteur croit voir la page, alors qu'il voit un tirage
 * parmi des milliers. La moyenne par bande, elle, décrit le réglage.
 */
function PreviewSummary({ preview, bands, onReroll }) {
  if (!preview || !preview.draws?.length) return null;

  const draws = preview.draws;
  const perBand = {};
  draws.forEach((draw) => {
    draw.forEach((decisionId) => {
      const band = preview.classification?.[decisionId]?.band;
      if (band) perBand[band] = (perBand[band] || 0) + 1;
    });
  });

  const raised = new Set();
  (preview.notes || []).forEach((sample) => sample.forEach((n) => raised.add(n.code)));

  return (
    <>
      <h3 className="builder-subsection-title">
        <span className="field-label-row">
          Ce que le réglage produit
          <InfoTooltip text="Moyenne observée sur 10 tirages, au premier tour d'une partie neuve. En cours de partie, les mesures déjà prises sortent du réservoir et la grille se resserre." />
        </span>
        <button className="builder-btn-secondary small" onClick={onReroll}>Autre tirage</button>
      </h3>
      <div className="builder-pool-bandcounts">
        {bands.map((band) => (
          <span key={band.id} className="builder-chip">
            {band.label || band.id} · {((perBand[band.id] || 0) / draws.length).toFixed(1)} par tour
          </span>
        ))}
      </div>
      {[...raised].map((code) => (
        <div key={code} className="builder-warning-inline">
          ⚠ Sur ces 10 tirages : {NOTE_LABELS[code] || code}.
        </div>
      ))}
    </>
  );
}
